import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@elkaso-app/web-design';
import { EmptyTableState } from 'components/base-table/components';
import { useTable } from 'react-table';

interface IExcelTable {
  columns: any;
  data: any;
  initialHiddenColumns?: string[];
  renderHeader?: boolean;
  /** Cap the body height; the header stays sticky while rows scroll. */
  maxHeight?: string;
}

/**
 * A lean, spreadsheet-styled read table: gridlines on every cell, dense padding,
 * and a sticky compact header inside a bordered scroll container.
 *
 * It mirrors BaseTable's react-table API (columns/data/initialHiddenColumns) so it is a
 * drop-in for read views, but intentionally omits BaseTable's drag-to-reorder and row
 * striping — use BaseTable when you need those. Shared so any list can opt into the
 * Excel look without changing the global BaseTable.
 */
export const ExcelTable = ({
  columns,
  data,
  initialHiddenColumns = [],
  renderHeader = true,
  maxHeight = '70vh',
}: IExcelTable) => {
  const { getTableProps, getTableBodyProps, rows, prepareRow, headerGroups } = useTable({
    columns,
    data,
    initialState: {
      hiddenColumns: [...initialHiddenColumns],
    },
  });

  return (
    <Box overflowX='auto' overflowY='auto' maxH={maxHeight} border='1px solid' borderColor='gray.200' borderRadius='md'>
      <Table
        {...getTableProps()}
        size='sm'
        sx={{
          // Excel-like density: gridlines on every cell, tight padding, sticky compact header.
          'th, td': {
            borderWidth: '1px',
            borderColor: 'gray.200',
            px: '2',
            py: '1',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
          },
          th: {
            bg: 'gray.50',
            textTransform: 'none',
            fontSize: 'xs',
            letterSpacing: 'normal',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
          'tbody tr:hover td': { bg: 'gray.50' },
        }}>
        {!!renderHeader && (
          <Thead>
            {headerGroups?.map((headerGroup, i) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers?.map((column, j) => (
                  <Th {...column.getHeaderProps()} key={j}>
                    {column.render('Header')}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
        )}

        <Tbody {...getTableBodyProps()}>
          {!rows.length && <EmptyTableState />}

          {rows?.map((row, i) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={i}>
                {row.cells.map((cell, j) => (
                  <Td {...cell.getCellProps()} key={j}>
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
